import type { CubeFilter, CubeFilterOperator, CubeQuery, CubeTimeDimension } from '@/dev/types/cubeQueryTypes';

const filterOperators: CubeFilterOperator[] = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'gt',
  'gte',
  'lt',
  'lte',
  'set',
  'notSet',
  'inDateRange',
  'notInDateRange',
  'beforeDate',
  'beforeOrOnDate',
  'afterDate',
  'afterOrOnDate',
];

const timeGranularities: NonNullable<CubeTimeDimension['granularity']>[] = [
  'second',
  'minute',
  'hour',
  'day',
  'week',
  'month',
  'quarter',
  'year',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(memberName => typeof memberName === 'string');

const isFilterOperator = (value: unknown): value is CubeFilterOperator =>
  typeof value === 'string' && filterOperators.some(operator => operator === value);

const isDateRange = (value: unknown): boolean =>
  typeof value === 'string' ||
  (Array.isArray(value) && value.length === 2 && value.every(date => typeof date === 'string'));

const isCubeFilter = (value: unknown): value is CubeFilter => {
  if (!isRecord(value)) return false;

  if (typeof value.member === 'string' && isFilterOperator(value.operator)) {
    return value.values === undefined || isStringArray(value.values);
  }

  if (Array.isArray(value.and)) return value.and.every(isCubeFilter);
  if (Array.isArray(value.or)) return value.or.every(isCubeFilter);

  return false;
};

const isFilterArray = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.every(
    candidateFilter => candidateFilter === null || candidateFilter === undefined || isCubeFilter(candidateFilter),
  );

const isTimeDimension = (value: unknown): value is CubeTimeDimension => {
  if (!isRecord(value) || typeof value.dimension !== 'string') return false;

  return (
    (value.granularity === undefined || timeGranularities.some(granularity => granularity === value.granularity)) &&
    (value.dateRange === undefined || isDateRange(value.dateRange))
  );
};

const isOrder = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.every(
      order =>
        Array.isArray(order) &&
        order.length === 2 &&
        typeof order[0] === 'string' &&
        (order[1] === 'asc' || order[1] === 'desc'),
    );
  }

  return isRecord(value) && Object.values(value).every(direction => direction === 'asc' || direction === 'desc');
};

export const isCubeQuery = (value: unknown): value is CubeQuery => {
  if (!isRecord(value)) return false;

  const hasMembers =
    (Array.isArray(value.measures) && value.measures.length > 0) ||
    (Array.isArray(value.dimensions) && value.dimensions.length > 0) ||
    (Array.isArray(value.segments) && value.segments.length > 0) ||
    (Array.isArray(value.timeDimensions) && value.timeDimensions.length > 0);

  if (!hasMembers) return false;

  return (
    (value.measures === undefined || isStringArray(value.measures)) &&
    (value.dimensions === undefined || isStringArray(value.dimensions)) &&
    (value.segments === undefined || isStringArray(value.segments)) &&
    (value.filters === undefined || isFilterArray(value.filters)) &&
    (value.timeDimensions === undefined ||
      (Array.isArray(value.timeDimensions) && value.timeDimensions.every(isTimeDimension))) &&
    (value.limit === undefined || (typeof value.limit === 'number' && Number.isFinite(value.limit))) &&
    (value.offset === undefined || (typeof value.offset === 'number' && Number.isFinite(value.offset))) &&
    (value.order === undefined || isOrder(value.order)) &&
    (value.timezone === undefined || typeof value.timezone === 'string') &&
    (value.renewQuery === undefined || typeof value.renewQuery === 'boolean') &&
    (value.ungrouped === undefined || typeof value.ungrouped === 'boolean')
  );
};
